import { cn } from "@/lib/utils"
import { useEffect, useMemo, useRef, type ReactNode } from "react"
import { PRINT_CSS_SCOPE, scopeCss } from "../declaration-templates/scopeCss"
import {
  DEFAULT_SHEET_GEOMETRY,
  layoutSheets,
  type SheetGeometry,
} from "./paginate"

/** Content and size changes arrive in bursts (a keystroke, a fragment
 *  re-render, a panel resize); the pass runs once they settle. */
const LAYOUT_DEBOUNCE_MS = 150

/** The one place the declaration print stylesheet (`declaration.css`, fonts
 *  included) is applied on screen: scoped under `.tpl-scope` so its global
 *  `body`/`p`/`table` rules stay inside, on the grey backdrop the
 *  stylesheet's own `@media screen` block paints, with the A4 paper centred
 *  by that same block. Both editors — the per-declaration block editor and
 *  the template editor — render their Tiptap document inside this, so a
 *  document looks the same in either and the same as the PDF. The document
 *  is paginated into sheets (paginate.ts) whenever its content or size
 *  changes. */
export function PaperScope({
  css,
  children,
  className,
  extraCss,
  geometry = DEFAULT_SHEET_GEOMETRY,
  showSheetCounter = false,
  stripHtml,
  paginate = true,
}: {
  css: string
  children: ReactNode
  className?: string
  /** Editor-only rules appended after the scoped stylesheet. */
  extraCss?: string
  /** The document's own page geometry (`pageGeometry.marginsMm`). Pass a
   *  stable object — a new one re-subscribes the observers. */
  geometry?: SheetGeometry
  /** Number every sheet on its band ("2 / 5"). */
  showSheetCounter?: boolean
  /** What the PDF prints in the page margins, for a document whose tree
   *  carries no strip NODES (design §8, review I3): the conformity block
   *  editor, where the strips live outside the editable tree but the PDF
   *  prints at least the legacy footer on every page. The template editor
   *  passes nothing — its strips are real, live nodes. Pass a stable object;
   *  a new one re-subscribes the observers. */
  stripHtml?: { header?: string | null; footer?: string | null }
  /** Split the paper into sheets (paginate.ts). Off: one continuous paper —
   *  the template editor, whose blocks the user adds, removes and drags. */
  paginate?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const styleTag = useMemo(
    () => `${scopeCss(css, `.${PRINT_CSS_SCOPE}`)}\n${extraCss ?? ""}`,
    [css, extraCss],
  )

  useEffect(() => {
    const root = ref.current
    if (!paginate || !root || typeof ResizeObserver === "undefined") return
    let timer: ReturnType<typeof setTimeout> | null = null
    let frame = 0
    const apply = () => {
      const paper = root.querySelector<HTMLElement>(".paper")
      if (paper) layoutSheets(paper, geometry, { showSheetCounter, stripHtml })
    }
    const run = () => {
      if (timer) clearTimeout(timer)
      cancelAnimationFrame(frame)
      timer = setTimeout(() => {
        // Measure at a frame boundary, after the browser has laid the
        // burst of changes out.
        frame = requestAnimationFrame(apply)
      }, LAYOUT_DEBOUNCE_MS)
    }
    const resize = new ResizeObserver(run)
    // Not `attributes`: the margins and paddings the pass writes are
    // attribute changes.
    const mutate = new MutationObserver(run)
    resize.observe(root)
    mutate.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    })
    // The first pass is not debounced — the document would show unpaginated
    // for 150ms otherwise. What this pass itself writes (bands, spacer rows)
    // brings the observers back once; that second pass finds everything as
    // it wants it and writes nothing, so it settles there.
    apply()
    return () => {
      if (timer) clearTimeout(timer)
      cancelAnimationFrame(frame)
      resize.disconnect()
      mutate.disconnect()
    }
  }, [geometry, showSheetCounter, stripHtml, paginate])

  return (
    <div ref={ref} className={cn(PRINT_CSS_SCOPE, "min-h-full", className)}>
      <style>{styleTag}</style>
      {/* Horizontal breathing room around the paper; the stylesheet owns
          the vertical padding and the paper's own margins. */}
      <div className="px-8">{children}</div>
    </div>
  )
}
